export type RoadmapStatus =
  | "Completed"
  | "Ongoing"
  | "In progress"
  | "Planned"
  | "Pending";

export type RoadmapPhaseId =
  | "foundation"
  | "public_buildout"
  | "production_readiness"
  | "ecosystem_scale";

export type RoadmapFilterId =
  | "all"
  | "current"
  | "completed"
  | "upcoming";

export interface RoadmapPhase {
  id: RoadmapPhaseId;
  label: string;
  summary: string;
}

export interface RoadmapMilestone {
  id: number;
  phase: RoadmapPhaseId;
  timing: string;
  title: string;
  status: RoadmapStatus;
  note?: string;
  currentFocus?: boolean;
}

export const roadmapPhases: RoadmapPhase[] = [
  {
    id: "foundation",
    label: "Foundation",
    summary: "Core model design, legal setup, and structural groundwork.",
  },
  {
    id: "public_buildout",
    label: "Public Buildout",
    summary: "Documentation, public presence, product surface, and token sale execution.",
  },
  {
    id: "production_readiness",
    label: "Production Readiness",
    summary: "Architecture hardening, integrations, audit closure, and launch preparation.",
  },
  {
    id: "ecosystem_scale",
    label: "Ecosystem Scale",
    summary: "Live utility expansion, startup funding cycles, and broader onboarding.",
  },
];

export const roadmapFilters: Array<{ id: RoadmapFilterId; label: string }> = [
  { id: "all", label: "All milestones" },
  { id: "current", label: "Current focus" },
  { id: "completed", label: "Completed" },
  { id: "upcoming", label: "Upcoming" },
];

export const roadmapMilestones: RoadmapMilestone[] = [
  {
    id: 1,
    phase: "foundation",
    timing: "2017-2024",
    title: "Concept development and model design",
    status: "Completed",
  },
  {
    id: 2,
    phase: "foundation",
    timing: "2024",
    title: "Legal/entity structuring and organizational setup",
    status: "Completed",
  },
  {
    id: 3,
    phase: "public_buildout",
    timing: "Q1 2025",
    title: "Whitepaper and litepaper publication",
    status: "Completed",
  },
  {
    id: 4,
    phase: "public_buildout",
    timing: "Q1-Q4 2025",
    title: "Public positioning, ecosystem narrative, and awareness building",
    status: "Ongoing",
  },
  {
    id: 5,
    phase: "public_buildout",
    timing: "2025",
    title: "MVP refinement and public dashboard/product surface",
    status: "Completed",
    note: "Public surface partially live.",
  },
  {
    id: 6,
    phase: "public_buildout",
    timing: "Q4 2025",
    title: "Testnet / controlled beta expansion",
    status: "Completed",
  },
  {
    id: 7,
    phase: "public_buildout",
    timing: "Q1 2026",
    title: "Token sale / IEO execution",
    status: "Ongoing",
    currentFocus: true,
  },
  {
    id: 8,
    phase: "public_buildout",
    timing: "Feb 2026",
    title: "Pionex exchange listing",
    status: "Completed",
  },
  {
    id: 9,
    phase: "public_buildout",
    timing: "2026",
    title: "Additional exchange listings",
    status: "Ongoing",
    note: "Pionex is completed, with more listings in progress.",
    currentFocus: true,
  },
  {
    id: 10,
    phase: "public_buildout",
    timing: "Q1 2026",
    title: "Post-sale roadmap and timeline reconciliation",
    status: "Pending",
    note: "Awaiting a published alignment update.",
  },
  {
    id: 11,
    phase: "production_readiness",
    timing: "Q2 2026",
    title: "Production architecture finalization",
    status: "In progress",
    note: "Scope refined for production readiness.",
    currentFocus: true,
  },
  {
    id: 12,
    phase: "production_readiness",
    timing: "Q2 2026",
    title: "Governance framework hardening",
    status: "In progress",
    note: "Governance controls are being tightened.",
    currentFocus: true,
  },
  {
    id: 14,
    phase: "production_readiness",
    timing: "Q3 2026",
    title: "Final security audit and remediation closure",
    status: "Planned",
    note: "Scheduled for the final readiness phase.",
  },
  {
    id: 15,
    phase: "production_readiness",
    timing: "Late Q3 2026",
    title: "Launch readiness review",
    status: "Planned",
  },
  {
    id: 16,
    phase: "production_readiness",
    timing: "Q4 2026",
    title: "Mainnet launch",
    status: "Planned",
    note: "Target window revised.",
  },
  {
    id: 17,
    phase: "production_readiness",
    timing: "Q4 2026",
    title: "Live on-chain governance at production scale",
    status: "Planned",
  },
  {
    id: 18,
    phase: "ecosystem_scale",
    timing: "Q1 2027",
    title: "First publicly documented startup funding cycle",
    status: "Planned",
  },
  {
    id: 19,
    phase: "ecosystem_scale",
    timing: "Q1-Q2 2027",
    title: "Liquidity and exchange expansion tied to live utility",
    status: "In progress",
    note: "Agreement work can advance before utility goes live.",
  },
  {
    id: 20,
    phase: "ecosystem_scale",
    timing: "2027",
    title: "Ecosystem scaling and wider startup onboarding",
    status: "Planned",
  },
];
